import React from 'react';
import {
  FormGroup, Label, Input, Button, ButtonGroup,
  ListGroup, ListGroupItem, InputGroup, InputGroupAddon,
} from 'reactstrap';
import { REGISTRY, CATS, TYPE_FAMILY, familyKey, switchTypePatch, switchTypeLoss, newRowKey } from './registry';
import ImageUpload from './ImageUpload';

// Registry-driven inspector built from reactstrap components.
// Props: section, platform, onChange(patch), onChangeType(type), onOpenSource(kind, chipIdx?), onRemove
// NOTE: the CMS bootstrap build has no custom-control/custom-switch CSS, so we use
// ButtonGroup On/Off toggles and native checkboxes instead of CustomInput switches.

function Pills({ items, onPick, ariaLabel }) {
  return (
    <ButtonGroup size="sm" role="group" aria-label={ariaLabel}>
      {items.map(([v, label, on]) => (
        <Button key={v} color={on ? 'primary' : 'secondary'} outline={!on} aria-pressed={on} onClick={() => onPick(v)}>{label}</Button>
      ))}
    </ButtonGroup>
  );
}

export default function Inspector({ section: s, platform, cats = CATS, onChange, onChangeType, onOpenSource, onRemove }) {
  if (!s) {
    return (
      <div className="text-center text-muted" style={{ padding: '40px 14px', fontSize: '.85rem' }}>
        <i className="fa fa-hand-pointer-o fa-2x d-block mb-2" />Select a section to edit, or add a new one.
      </div>
    );
  }
  const r = REGISTRY[s.type] || { label: s.type, opts: ['title'] };
  const chips = s.chips || [];
  const social = s.social || [];
  const fid = (k) => `hpb-${s._id}-${k}`; // unique id per field for label association

  const setChips = (next) => onChange({ chips: next });
  const setSocial = (next) => onChange({ social: next });
  const setOverlay = (k, v) => onChange({ overlay: Object.assign({}, s.overlay, { [k]: v }) });

  const ctrl = (o) => {
    const kind = o.split(':')[0];
    switch (kind) {
      case 'cat':
        if (platform !== 'app') return null; // category tabs are app-only
        return (
          <FormGroup key={o}>
            <Label for={fid('cat')} className="font-weight-bold" style={{ fontSize: '.78rem' }}>Category tab</Label>
            <Input id={fid('cat')} type="select" bsSize="sm" value={s.homepage_category || 'allCategory'} onChange={(e) => onChange({ homepage_category: e.target.value })}>
              {cats.map((c) => <option key={c[0]} value={c[0]}>{c[1]}</option>)}
            </Input>
          </FormGroup>
        );
      case 'title':
        return <FormGroup key={o}><Label for={fid('title')} className="font-weight-bold" style={{ fontSize: '.78rem' }}>Title</Label><Input id={fid('title')} bsSize="sm" value={s.title || ''} onChange={(e) => onChange({ title: e.target.value })} /></FormGroup>;
      case 'subtitle':
        // banner_chip subtitle is web-only: the Flutter banner_chip widget renders only the title.
        if (platform === 'app' && s.type.indexOf('banner_chip') === 0) return null;
        return <FormGroup key={o}><Label for={fid('subtitle')} className="font-weight-bold" style={{ fontSize: '.78rem' }}>Subtitle</Label><Input id={fid('subtitle')} bsSize="sm" value={s.subtitle || ''} onChange={(e) => onChange({ subtitle: e.target.value })} /></FormGroup>;
      case 'action':
        return <FormGroup key={o}><Label for={fid('action')} className="font-weight-bold" style={{ fontSize: '.78rem' }}>Action label</Label><Input id={fid('action')} bsSize="sm" value={s.action_title || ''} placeholder="View All" onChange={(e) => onChange({ action_title: e.target.value })} /></FormGroup>;
      case 'cardStyle': {
        const small = s.type.indexOf('small') !== -1;
        return <FormGroup key={o}><Label className="font-weight-bold d-block" style={{ fontSize: '.78rem' }}>Card style</Label><Pills ariaLabel="Card style" items={[['small', 'Boxed card', small], ['nobg', 'No background', !small]]} onPick={(v) => onChangeType(s.type.replace('_small_card', '').replace('_no_bg_card', '') + (v === 'small' ? '_small_card' : '_no_bg_card'))} /></FormGroup>;
      }
      case 'rows': {
        const two = s.type === 'banner_chip_2_rows';
        return <FormGroup key={o}><Label className="font-weight-bold d-block" style={{ fontSize: '.78rem' }}>Product rows</Label><Pills ariaLabel="Product rows" items={[['2', '2 rows', two], ['1', '1 row', !two]]} onPick={(v) => onChangeType(v === '2' ? 'banner_chip_2_rows' : 'banner_chip_1_row')} /></FormGroup>;
      }
      case 'showRetail':
        // Per-section toggle is web-only: the Flutter app ignores it and uses a global
        // `show_under_retail` setting, so hide this inert control in the app builder.
        if (platform === 'app') return null;
        return (
          <FormGroup key={o}>
            <Label className="font-weight-bold d-block" style={{ fontSize: '.78rem' }}>Show “under retail” price tags</Label>
            <Pills ariaLabel="Show under retail price tags" items={[['on', 'On', !!s.show_under_retail], ['off', 'Off', !s.show_under_retail]]} onPick={(v) => onChange({ show_under_retail: v === 'on' })} />
          </FormGroup>
        );
      case 'image': {
        // Web has desktop + mobile viewports → two crops, both required. The app is mobile-only
        // and reads a single banner_image, so it gets one slot. Ratios are the live renderer's:
        // banner_chip = 16:5 desktop / 20:9 mobile; break = full-width cover background (no fixed ratio).
        if (s.type === 'banner_only') {
          // Labels mirror the wire contract: web publishes banner_image_landscape/_portrait,
          // the app publishes the single banner_image.
          if (platform !== 'web') {
            return <ImageUpload key={o} label="Banner image" url={s.bannerImg || ''} onChange={(v) => onChange({ bannerImg: v })} hint="App banner · full-width image. JPG/PNG/WEBP/GIF." />;
          }
          return (
            <React.Fragment key={o}>
              <ImageUpload label="Banner image (landscape)" url={s.bannerImg || ''} onChange={(v) => onChange({ bannerImg: v })} hint="Desktop web · wide landscape banner. JPG/PNG/WEBP/GIF." />
              <ImageUpload label="Banner image (portrait)" url={s.bannerImgMobile || ''} onChange={(v) => onChange({ bannerImgMobile: v })} hint="Mobile web · portrait crop. JPG/PNG/WEBP/GIF." />
            </React.Fragment>
          );
        }
        const isBC = s.type.indexOf('banner_chip') === 0;
        if (platform !== 'web') {
          // App ratios (Flutter renderer): banner_chip 20:9 (358:161), cover; break = full-width cover.
          return <ImageUpload key={o} label="Image" url={s.bannerImg || ''} onChange={(v) => onChange({ bannerImg: v })} hint={isBC ? 'App banner · 20:9 ratio (e.g. 716×322). JPG/PNG/WEBP/GIF.' : 'App background · full-width cover image.'} />;
        }
        return (
          <React.Fragment key={o}>
            <ImageUpload label="Desktop image" url={s.bannerImg || ''} onChange={(v) => onChange({ bannerImg: v })} hint={isBC ? 'Desktop web · 16:5 ratio (e.g. 1440×450). JPG/PNG/WEBP/GIF.' : 'Desktop web · wide cover background (e.g. 1600px wide).'} />
            <ImageUpload label="Mobile image" url={s.bannerImgMobile || ''} onChange={(v) => onChange({ bannerImgMobile: v })} hint={isBC ? 'Mobile web · 20:9 ratio (e.g. 800×360). JPG/PNG/WEBP/GIF.' : 'Mobile web · cover background (e.g. 900px wide).'} />
          </React.Fragment>
        );
      }
      case 'icon':
        return <ImageUpload key={o} label="Icon" url={s.icon || ''} onChange={(v) => onChange({ icon: v })} hint="Small icon (≈ 28px square) beside the headline." />;
      case 'bannerLayout': {
        const m = s.bannerLayout || 'banner_chips';
        return <FormGroup key={o}><Label className="font-weight-bold d-block" style={{ fontSize: '.78rem' }}>Layout</Label><Pills ariaLabel="Banner layout" items={[['banner_chips', 'Banner + chips', m === 'banner_chips'], ['banner', 'Banner only', m === 'banner'], ['chips', 'Chips only', m === 'chips']]} onPick={(v) => onChange({ bannerLayout: v })} /></FormGroup>;
      }
      case 'chips':
        return (
          <FormGroup key={o}>
            <Label className="font-weight-bold" style={{ fontSize: '.78rem' }}>Filter chips (max 10)</Label>
            {chips.map((c, k) => (
              <InputGroup size="sm" className="mb-1" key={c._k || k}>
                <Input bsSize="sm" placeholder="Chip label" value={c.name || ''} onChange={(e) => { const n = chips.map((x) => Object.assign({}, x)); n[k].name = e.target.value; setChips(n); }} />
                <InputGroupAddon addonType="append">
                  <Button color={c.endpoint ? 'info' : 'secondary'} outline aria-label={c.endpoint ? `Edit filter for chip ${c.name || k + 1}` : `Set filter for chip ${c.name || k + 1}`} title={c.endpoint ? `${c.endpoint_type}: ${c.endpoint}` : 'Set chip filter'} onClick={() => onOpenSource('data', k)}><i className="fa fa-filter" aria-hidden="true" /></Button>
                  <Button color="danger" outline aria-label={`Remove chip ${c.name || k + 1}`} onClick={() => { const n = chips.map((x) => Object.assign({}, x)); n.splice(k, 1); setChips(n); }}><i className="fa fa-times" aria-hidden="true" /></Button>
                </InputGroupAddon>
              </InputGroup>
            ))}
            <Button size="sm" color="primary" outline className="mt-2 d-block" disabled={chips.length >= 10} onClick={() => setChips(chips.concat([{ name: 'New chip', _k: newRowKey() }]))}><i className="fa fa-plus" /> Add chip ({chips.length}/10)</Button>
            <small className="text-muted d-block mt-1">Each chip filters the product list — set its source with the <i className="fa fa-filter" /> button.</small>
          </FormGroup>
        );
      case 'overlay': {
        if (platform !== 'web') return null; // the app model has no banner_overlay field
        const ov = s.overlay || {};
        return (
          <FormGroup key={o}>
            <Label className="font-weight-bold" style={{ fontSize: '.78rem' }}>Banner text overlay <span className="badge badge-light">web only</span></Label>
            <Input bsSize="sm" className="mb-1" placeholder="Badge (e.g. NEW)" value={ov.badge || ''} onChange={(e) => setOverlay('badge', e.target.value)} />
            <Input bsSize="sm" className="mb-1" placeholder="Overlay title" value={ov.title || ''} onChange={(e) => setOverlay('title', e.target.value)} />
            <Input bsSize="sm" placeholder="Overlay subtitle" value={ov.subtitle || ''} onChange={(e) => setOverlay('subtitle', e.target.value)} />
          </FormGroup>
        );
      }
      case 'social':
        return (
          <FormGroup key={o}>
            <Label className="font-weight-bold" style={{ fontSize: '.78rem' }}>Social proof (min 3)</Label>
            {social.map((m, k) => (
              <InputGroup size="sm" className="mb-1" key={m._k || k}>
                <Input bsSize="sm" style={{ maxWidth: 90 }} placeholder="10K+" value={m.number || ''} onChange={(e) => { const n = social.map((x) => Object.assign({}, x)); n[k].number = e.target.value; setSocial(n); }} />
                <Input bsSize="sm" placeholder="shipped" value={m.text || ''} onChange={(e) => { const n = social.map((x) => Object.assign({}, x)); n[k].text = e.target.value; setSocial(n); }} />
                <InputGroupAddon addonType="append"><Button color="danger" outline aria-label={`Remove metric ${k + 1}`} onClick={() => { const n = social.map((x) => Object.assign({}, x)); n.splice(k, 1); setSocial(n); }}><i className="fa fa-times" aria-hidden="true" /></Button></InputGroupAddon>
              </InputGroup>
            ))}
            <Button size="sm" color="secondary" outline onClick={() => setSocial(social.concat([{ number: '', text: '', _k: newRowKey() }]))}><i className="fa fa-plus" /> Add metric</Button>
            {social.filter((m) => m.number && m.text).length < 3 ? <small className="text-danger d-block mt-1">Needs at least 3 (each with a number AND label) — the section hides otherwise.</small> : null}
          </FormGroup>
        );
      case 'promoItems':
        return (
          <FormGroup key={o}>
            <Label className="font-weight-bold" style={{ fontSize: '.78rem' }}>Promo slides</Label>
            <small className="text-muted d-block">{(s.promoItems || []).length} slide(s). Each = image + link. Full editor coming; existing slides are preserved on save.</small>
          </FormGroup>
        );
      case 'source': {
        const sk = o.split(':')[1] || 'data';
        const et = s.endpoint_type || (sk !== 'data' ? sk : '');
        const label = s.source || (sk === 'brand' ? 'Popular brands' : sk === 'slider' ? 'Slider set' : sk === 'shortcut' ? 'Shortcut config' : 'No content selected');
        return (
          <FormGroup key={o}>
            <Label className="font-weight-bold" style={{ fontSize: '.78rem' }}>Content source</Label>
            <ListGroup>
              <ListGroupItem action tag="button" type="button" onClick={() => onOpenSource(sk)} className="d-flex align-items-center">
                <i className="fa fa-database text-muted mr-2" />
                <div className="flex-fill text-truncate">
                  <div style={{ fontWeight: 600 }} className="text-truncate">{label}</div>
                  {et ? <small className="text-muted">{et}{s.slug ? ` · ${s.slug}` : ''}</small> : <small className="text-muted">click to choose</small>}
                </div>
                <i className="fa fa-chevron-right text-muted" />
              </ListGroupItem>
            </ListGroup>
          </FormGroup>
        );
      }
      default:
        return null;
    }
  };

  const fam = familyKey(s.type);
  return (
    <div>
      {/* Only the family branch renders a select, so only it gets a real <label for> —
          a non-family type shows a static name, and a dangling `for` would be worse. */}
      {fam
        ? <Label for={fid('type')} className="mb-0"><small className="text-muted text-uppercase">Template</small></Label>
        : <small className="text-muted text-uppercase">Template</small>}
      {fam ? (
        <FormGroup className="mt-1 mb-2">
          <Input id={fid('type')} type="select" bsSize="sm" value={fam} onChange={(e) => {
            const target = e.target.value;
            const patch = switchTypePatch(s, target);
            const lost = switchTypeLoss(s, patch);
            // Only guard a switch that actually discards authored content: the sole undo is
            // Discard, which reloads the layout and drops every unsaved edit in the session.
            // A native select also fires change on arrow-key navigation, so this is easy to hit.
            if (lost.length) {
              const label = (TYPE_FAMILY.find(([k]) => k === target) || [null, target])[1];
              if (!window.confirm(`Switching to ${label} clears ${lost.join(', ')} on this section. Continue?`)) {
                e.target.value = fam; // no state change means no re-render, so restore the DOM value
                return;
              }
            }
            onChange(patch);
          }}>
            {TYPE_FAMILY.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
          </Input>
          {/* The badge annotates the TEMPLATE, so it stays with the value — not with the
              caption, and not between the label and the select it labels. */}
          <small className="text-muted">Switching keeps the title and content source; fields the new template doesn’t use are cleared.</small>
          {' '}<span className="badge badge-light">web · app</span>
        </FormGroup>
      ) : (
        <div className="font-weight-bold mb-2">{r.label} <span className="badge badge-light">web · app</span></div>
      )}
      {(r.opts || ['title']).map((o) => ctrl(o))}
      <hr />
      <Button size="sm" color="danger" outline block onClick={onRemove}><i className="fa fa-trash" /> Remove section</Button>
    </div>
  );
}
